import {
  SocketEvents,
  FileSummarizeData,
  AIGenerationEndPayload,
} from "common";
import { Socket } from "socket.io-client";

/**
 * Waits for socket connection with timeout and error handling
 */
export function waitForSocketConnection(
  socket: Socket,
  timeoutMs: number = 10000
): Promise<void> {
  return new Promise((resolve, reject) => {
    if (socket.connected) {
      resolve();
      return;
    }

    const connectionTimeout = setTimeout(() => {
      socket.off("connect", handleConnect);
      socket.off("connect_error", handleConnectError);
      socket.off("disconnect", handleDisconnect);
      reject(
        new Error(
          "Connection timeout: Could not connect to server. Please check your network connection and try again."
        )
      );
    }, timeoutMs);

    const handleConnect = () => {
      clearTimeout(connectionTimeout);
      socket.off("connect", handleConnect);
      socket.off("connect_error", handleConnectError);
      socket.off("disconnect", handleDisconnect);
      resolve();
    };

    const handleConnectError = (error: Error) => {
      clearTimeout(connectionTimeout);
      socket.off("connect", handleConnect);
      socket.off("connect_error", handleConnectError);
      socket.off("disconnect", handleDisconnect);
      reject(
        new Error(
          `Connection failed: ${error.message || "Could not connect to server. Please try again."}`
        )
      );
    };

    const handleDisconnect = () => {
      clearTimeout(connectionTimeout);
      socket.off("connect", handleConnect);
      socket.off("connect_error", handleConnectError);
      socket.off("disconnect", handleDisconnect);
      reject(new Error("Connection lost. Please try again."));
    };

    socket.on("connect", handleConnect);
    socket.on("connect_error", handleConnectError);
    socket.on("disconnect", handleDisconnect);

    if (socket.disconnected) {
      socket.connect();
    }
  });
}

interface ResponseHandlers {
  onGenerate: (content: string) => void;
  onComplete: (payload?: AIGenerationEndPayload) => void;
  onError: (error: { message: string; error?: string }) => void;
}

/**
 * Sets up socket listeners for AI response streaming
 */
export function setupResponseListeners(
  socket: Socket,
  handlers: ResponseHandlers
): () => void {
  const handleAIGenerate = (content: string) => {
    handlers.onGenerate(content);
  };

  const handleAIGenerationEnd = (payload?: AIGenerationEndPayload) => {
    handlers.onComplete(payload);
    cleanup();
  };

  const handleReportChatError = (error: {
    message: string;
    error?: string;
  }) => {
    handlers.onError(error);
    cleanup();
  };

  const handleAIError = (error: { message: string }) => {
    handlers.onError({
      message: error.message || "AI error occurred",
      error: error.message,
    });
    cleanup();
  };

  const cleanup = () => {
    socket.off(SocketEvents.AIGenerate, handleAIGenerate);
    socket.off(SocketEvents.AIGenerationEnd, handleAIGenerationEnd);
    socket.off(SocketEvents.ReportChatError, handleReportChatError);
    socket.off(SocketEvents.AIError, handleAIError);
  };

  socket.on(SocketEvents.AIGenerate, handleAIGenerate);
  socket.on(SocketEvents.AIGenerationEnd, handleAIGenerationEnd);
  socket.on(SocketEvents.ReportChatError, handleReportChatError);
  socket.on(SocketEvents.AIError, handleAIError);

  return cleanup;
}

interface SendQuestionOptions {
  question: string;
  tokenId: number;
  filePath?: string;
  runUUID?: string;
  conversationHistory?: Array<{
    role: "user" | "assistant" | "system";
    content: string;
  }>;
  model?: string;
}

/**
 * Sends a question via socket and returns a promise that resolves with the complete response
 */
export async function sendReportChatQuestion(
  socket: Socket,
  options: SendQuestionOptions,
  timeoutMs: number = 60000
): Promise<string> {
  return new Promise<string>((resolve, reject) => {
    if (!socket || !socket.connected) {
      reject(new Error("Socket not connected after waiting"));
      return;
    }

    let response = "";
    let responseComplete = false;

    const cleanup = setupResponseListeners(socket, {
      onGenerate: (content: string) => {
        response += content;
      },
      onComplete: () => {
        responseComplete = true;
        resolve(response);
      },
      onError: (error: { message: string; error?: string }) => {
        const errorMessage =
          error?.error || error?.message || "AI error occurred";
        reject(new Error(errorMessage));
      },
    });

    const reportChatData = {
      filePath: options.filePath || "",
      runUUID: options.runUUID || "",
      question: options.question,
      conversationHistory: options.conversationHistory || [],
      tokenId: options.tokenId,
      model: options.model,
    };

    socket.emit(SocketEvents.ReportChatQuestion, reportChatData);

    // Timeout fallback
    setTimeout(() => {
      if (!responseComplete) {
        cleanup();
        if (response) {
          resolve(response);
        } else {
          reject(new Error("Request timeout"));
        }
      }
    }, timeoutMs);
  });
}

/**
 * Initializes file context by triggering file summarization
 */
export interface InitializeFileContextOptions {
  filePath: string;
  runUUID: string;
  tokenId: number;
  onProgress?: (message: string) => void;
  timeoutMs?: number;
  fileSource?: FileSummarizeData["fileSource"];
}

export async function initializeFileContext(
  socket: Socket,
  options: InitializeFileContextOptions
): Promise<void> {
  const timeoutMs = options.timeoutMs ?? 300000; // 5 minutes - very generous, relying on progress events
  const fileSource: FileSummarizeData["fileSource"] =
    options.fileSource ?? "report";

  return new Promise<void>((resolve, reject) => {
    if (!socket || !socket.connected) {
      reject(new Error("Socket not connected"));
      return;
    }

    let summaryComplete = false;

    const handleAIGenerate = (content: string) => {
      // Emit progress update to frontend (optional callback)
      if (options.onProgress && typeof content === "string") {
        options.onProgress(content);
      }
    };

    const handleAIGenerationEnd = () => {
      summaryComplete = true;
      cleanup();
      resolve();
    };

    const handleSummarizeError = (error: {
      message: string;
      error?: string;
    }) => {
      cleanup();
      reject(
        new Error(
          error?.error || error?.message || "Failed to initialize file context"
        )
      );
    };

    const cleanup = () => {
      socket.off(SocketEvents.AIGenerate, handleAIGenerate);
      socket.off(SocketEvents.AIGenerationEnd, handleAIGenerationEnd);
      socket.off(SocketEvents.FileSummarizeError, handleSummarizeError);
    };

    socket.on(SocketEvents.AIGenerate, handleAIGenerate);
    socket.on(SocketEvents.AIGenerationEnd, handleAIGenerationEnd);
    socket.on(SocketEvents.FileSummarizeError, handleSummarizeError);

    const fileSummarizeData: FileSummarizeData = {
      filePath: options.filePath,
      runUUID: options.runUUID,
      tokenId: options.tokenId,
      fileSource,
    };

    socket.emit(SocketEvents.FileSummarize, fileSummarizeData);

    // Timeout fallback for preventing the request from hanging indefinitely
    setTimeout(() => {
      if (!summaryComplete) {
        cleanup();
        reject(
          new Error(
            "File processing timeout - please try again or contact support"
          )
        );
      }
    }, timeoutMs);
  });
}
